import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const rows = [
  {
    most: "File your return once a year and send you a bill",
    kgob: "Proactive tax planning throughout the year to minimize what you owe before filing season even starts"
  },
  {
    most: "Pick a filing status without explaining why it matters",
    kgob: "Analyze whether single, married filing jointly, married filing separately, or head of household saves you the most — and explain the impact in plain language"
  },
  {
    most: "Use cookie-cutter approaches regardless of your situation",
    kgob: "Custom strategies tailored to your specific life stage, income sources, and financial goals — whether you're an individual or a business owner"
  },
  {
    most: "Disappear between January and April, then rush through your return",
    kgob: "Year-round availability with a dedicated advisor who knows your situation inside and out"
  },
  {
    most: "Miss deductions because they don't ask about life changes",
    kgob: "We ask the questions others don't — about your marriage, children, home, investments, and retirement — because every detail affects your bottom line"
  },
  {
    most: "Hand you financial statements you can't actually read or use",
    kgob: "Clear, actionable financial insights that help you make smarter decisions every month"
  },
  {
    most: "Panic when you get an IRS notice and refer you to someone else",
    kgob: "In-house IRS representation team that handles disputes, audits, and negotiations directly on your behalf"
  }
];

export const ComparisonToggle = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => {
    setActive((p) => (p + 1) % rows.length);
  }, []);

  const prev = () => {
    setActive((p) => (p - 1 + rows.length) % rows.length);
    setPaused(true);
  };

  const goTo = (i) => {
    setActive(i);
    setPaused(true);
  };

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(next, 4000);
    return () => clearInterval(timer);
  }, [paused, next]);

  return (
    <section
      className="relative bg-[#0B2240] py-20 lg:py-28 overflow-hidden"
      data-testid="welcome-comparison"
    >
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '32px 32px'
      }} />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent" />

      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-semibold text-white">
            Not all CPAs are created equal.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-white/60">
            Here's what sets KGOB apart from the firm you're probably used to.
          </p>
        </motion.div>

        <div className="relative">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {/* Most CPAs */}
            <div className="rounded-2xl p-6 sm:p-8 border border-white/10 bg-white/5">
              <p className="uppercase tracking-widest text-xs text-white/40 mb-4 font-medium">
                What Most CPAs Do
              </p>
              <p className="text-white/70 text-base sm:text-lg leading-relaxed" data-testid="comparison-most">
                {rows[active].most}
              </p>
            </div>

            {/* KGOB */}
            <div className="rounded-2xl p-6 sm:p-8 border border-[#D4AF37]/30 bg-[#D4AF37]/8">
              <p className="uppercase tracking-widest text-xs text-[#D4AF37] mb-4 font-medium">
                What KGOB Does
              </p>
              <p className="text-white text-base sm:text-lg leading-relaxed font-medium" data-testid="comparison-kgob">
                {rows[active].kgob}
              </p>
            </div>
          </motion.div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={prev}
              className="p-2 rounded-full border border-white/20 text-white/60 hover:text-white hover:border-white/40 transition-colors"
              data-testid="comparison-prev"
              aria-label="Previous comparison"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            <div className="flex gap-2">
              {rows.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === active ? 'w-8 bg-[#D4AF37]' : 'w-2 bg-white/30 hover:bg-white/50'
                  }`}
                  aria-label={`Go to comparison ${i + 1}`}
                  data-testid={`comparison-dot-${i}`}
                />
              ))}
            </div>

            <button
              onClick={() => { next(); setPaused(true); }}
              className="p-2 rounded-full border border-white/20 text-white/60 hover:text-white hover:border-white/40 transition-colors"
              data-testid="comparison-next"
              aria-label="Next comparison"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
